import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
} from 'react';
import { toLiveHref } from '@/lib/toLiveHref';

type ButtonVariant = 'primary' | 'secondary';
type ButtonSize = 'md' | 'lg';

interface BaseProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
  className?: string;
  name?: string;
  id?: string;
}

type AnchorProps = BaseProps &
  AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };
type NativeButtonProps = BaseProps &
  ButtonHTMLAttributes<HTMLButtonElement> & { href?: undefined };
type ButtonProps = AnchorProps | NativeButtonProps;

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'border border-primary bg-primary text-white hover:bg-primary/90 disabled:bg-gray-900/40 disabled:border-gray-700/30',
  secondary:
    'border border-primary text-primary hover:bg-card-bg disabled:bg-gray-900/40',
};

/**
 * Both sizes share one height and differ only in padding and text size. It
 * resolves `--button-height`, not `--input-height`: a CTA and a form field are
 * no longer the same height (SH-474, 52px against 55px), and every CTA on the
 * page has to agree with the Sticky CTA Bar, which is the design reference.
 */
const sizeClasses: Record<ButtonSize, string> = {
  md: 'h-[var(--button-height,52px)] px-5 text-sm/5 md:text-md/8',
  lg: 'h-[var(--button-height,52px)] px-10 md:px-24 text-base/4 md:text-lg/8',
};

export const Button = (props: ButtonProps) => {
  const {
    variant = 'primary',
    size = 'md',
    className,
    children,
    ...rest
  } = props;
  const classes = classNames(
    // `cursor-pointer` explicitly: Tailwind v4's preflight dropped the
    // browser default for `<button>`. `disabled:cursor-not-allowed` below
    // still wins on a disabled one.
    'rounded-button focus-visible:ring-primary inline-flex cursor-pointer items-center justify-center gap-2 font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
    sizeClasses[size],
    variantClasses[variant],
    className,
  );

  if (rest.href !== undefined) {
    const { href, ...anchorRest } =
      rest as AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };
    return (
      <a className={classes} href={toLiveHref(href)} {...anchorRest}>
        {children}
      </a>
    );
  }

  const { type = 'button', ...buttonRest } =
    rest as ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button type={type} className={classes} {...buttonRest}>
      {children}
    </button>
  );
};
